import { useState, useMemo, useEffect } from 'react';
import { loadAllJobs, getAllTags } from '../services/markdownLoader';
import { Job } from '../services/markdownLoader';
import { getPaginationInfo, getPaginatedItems } from '../services/paginationUtils';
import { JobList } from '../components/JobList';
import { PaginationControls } from '../components/PaginationControls';
import { JobDetailModal } from '../components/JobDetailModal';

import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

import { Search, Filter } from 'lucide-react';

const DEFAULT_ITEMS_PER_PAGE = 10;
const STORAGE_KEY_ITEMS_PER_PAGE = 'jobListingsItemsPerPage';
const STORAGE_KEY_CURRENT_PAGE = 'jobListingsCurrentPage';

export function JobListingsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_CURRENT_PAGE);
    return saved ? parseInt(saved) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_ITEMS_PER_PAGE);
    return saved ? parseInt(saved) : DEFAULT_ITEMS_PER_PAGE;
  });

  // Load jobs and tags on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [jobs, tags] = await Promise.all([
          loadAllJobs(),
          getAllTags(),
        ]);
        setAllJobs(jobs);
        setAllTags(tags);
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save items per page to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ITEMS_PER_PAGE, itemsPerPage.toString());
  }, [itemsPerPage]);

  // Save current page to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_CURRENT_PAGE, currentPage.toString());
  }, [currentPage]);

  // Filter jobs based on search and tags
  const filteredJobs = useMemo(() => {
    let jobs = allJobs;

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      jobs = jobs.filter(
        job =>
          job.title.toLowerCase().includes(lowerQuery) ||
          job.company.toLowerCase().includes(lowerQuery) ||
          job.location.toLowerCase().includes(lowerQuery) ||
          job.tags.toLowerCase().includes(lowerQuery)
      );
    }

    if (selectedTags.length > 0) {
      jobs = jobs.filter(job => {
        const jobTags = job.tags.split(',').map(tag => tag.trim());
        return selectedTags.some(tag => jobTags.includes(tag));
      });
    }

    return jobs;
  }, [searchQuery, selectedTags, allJobs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedTags]);

  // Get paginated jobs
  const paginationInfo = getPaginationInfo(
    filteredJobs.length,
    itemsPerPage,
    currentPage
  );
  const paginatedJobs = getPaginatedItems(
    filteredJobs,
    itemsPerPage,
    currentPage
  );

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of job list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <Header />
      <div className="bg-amber-100/50 border-b border-gray-200 top-0 z-40 lg:bg-[url('/svg/bg.svg')] bg-cover bg-no-repeat bg-center">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-stone-500 mb-2 hidden md:block">Trabajos en Panamá</h1>
          <p className="text-gray-600">Encuentra tu oportunidad laboral con nuestra lista de empleos!</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Filter size={20} />
                Filtros
              </h2>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Buscar
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar trabajos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Tags Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Habilidades y profesiones
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {allTags.map((tag) => (
                    <label key={tag} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag)}
                        onChange={() => toggleTag(tag)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 hover:text-gray-900">
                        {tag}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {(searchQuery || selectedTags.length > 0) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTags([]);
                  }}
                  className="mt-6 w-full py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Limpiar todos los filtros
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-gray-600 font-medium">
                Mostrando <span className="font-bold text-gray-900">{paginatedJobs.length}</span> de{' '}
                <span className="font-bold text-gray-900">{filteredJobs.length}</span> vacante
                {filteredJobs.length !== 1 ? 's' : ''}
              </p>
              {searchQuery && (
                <p className="text-sm text-gray-500">
                  Search: <span className="font-semibold">"{searchQuery}"</span>
                </p>
              )}
            </div>
            <JobList
              jobs={paginatedJobs}
              onJobClick={(jobId) => {
                const job = allJobs.find(j => j.id === jobId);
                if (job) setSelectedJob(job);
              }}
              isEmpty={filteredJobs.length === 0}
              isLoading={false}
            />

            {/* Pagination Controls */}
            {paginationInfo.totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={paginationInfo.totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}

    <Footer />
    </div>
   
  );
}
